import { useAdminAccess } from "../hooks/useAdminAccess";
import { adminRecords, readAdminProject } from "./adminRecords";
import { agentSourceEntries } from "../../../src/shared/agentSources";
import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import type firebase from "firebase/app";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import update from "react-addons-update";
import { CopyToClipboard } from "react-copy-to-clipboard";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";
import Project from "../models/Project";
import { SudoButton } from "../sudokit/components/SudoButton";
import { SudoTooltip } from "../sudokit/components/SudoTooltip";
import SVG from "react-inlinesvg";
import CopyToClipboardButton from "./CopyToClipboardButton";
import UserCache from "./UserCache";
import { RepairProjectModal } from "../components/RepairProject/RepairProjectModal";


function ProjectExportPermission({ children }: { children: React.ReactNode }) {
  const { can } = useAdminAccess();
  return can("projects.export") ? <>{children}</> : null;
}

interface ImportProjectSectionProps {
  user: firebase.User | null | undefined;
}

interface ExportButtonProps {
  projectId: string;
  projectTitle: string;
}

interface ProjectsProps {
  showUserColumn?: boolean;
  userCache?: UserCache;
  user?: firebase.User | null;
}

type LegacyBeatsCleanupResponse = {
  success: boolean;
  dryRun: boolean;
  projectId: string;
  storyEngineId: string;
  currentBytes: number;
  afterBytes: number;
  scenesWithCommands: number;
  commandsBytes: number;
  coveredByPremigratedBeats: number;
  uncoveredScenes: Array<{ sceneId: string; documentId: string | null; commandBytes: number }>;
  canApply: boolean;
  applied?: boolean;
  archivedCount?: number;
  strippedCount?: number;
};

// Import Project Section Component
const ImportProjectSection = ({ user }: ImportProjectSectionProps) => {
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { setToast } = useToast();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === "application/zip" || file.name.endsWith(".zip")) {
        setSelectedFile(file);
      } else {
        setToast("Please select a valid ZIP file");
      }
    }
  };

  const handleImport = async () => {
    if (!selectedFile || isImporting || !user) return;

    setIsImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("userId", user.uid);

      const response = await Auth.postFilesWithIdToken("/api/import-complete-project", {}, formData);

      if (response.success) {
        setToast(`✅ Project "${response.project.title}" imported successfully!`);
        setSelectedFile(null);
        // Reset the file input
        const fileInput = document.getElementById("project-import-file") as HTMLInputElement | null;
        if (fileInput) fileInput.value = "";
        // Optionally refresh the page or trigger a re-fetch
        window.location.reload();
      } else {
        throw new Error(response.error || "Import failed");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setToast(`❌ Import failed: ${message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById("project-import-file") as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  return (
    <div className="flex items-center gap-3 h-[44px]">
      <SudoButton
        variant="secondary"
        size="small"
        className="h-8 px-3 text-xs"
        onClick={() => document.getElementById("project-import-file")?.click()}
        aria-label="Select project ZIP file to import"
      >
        📥 Import Project
      </SudoButton>
      <input
        id="project-import-file"
        type="file"
        accept=".zip"
        onChange={handleFileSelect}
        className="hidden"
      />

      {selectedFile && (
        <>
          <span
            className="text-xs text-gray-600 truncate max-w-[300px]"
            title={selectedFile.name}
          >
            {selectedFile.name}
          </span>
          <SudoButton
            onClick={handleImport}
            disabled={isImporting}
            variant="primary"
            size="small"
            className="h-8 px-3 text-xs"
            aria-label="Import selected project"
          >
            {isImporting ? "⏳ Importing..." : "📥 Import"}
          </SudoButton>
          <button
            onClick={clearFile}
            disabled={isImporting}
            className="text-gray-400 hover:text-gray-600 text-xs h-8 px-2 transition-colors"
            aria-label="Clear selected file"
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
};

// Export Button Component
const ExportButton = ({ projectId, projectTitle }: ExportButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const { setToast } = useToast();

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);

    try {
      // Use Auth.fetchFileWithIdToken for proper authentication
      const blob = await Auth.fetchFileWithIdToken("/api/export-complete-project", {
        projectId,
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `${(projectTitle || "project").replace(/[^a-zA-Z0-9]/g, "_")}_${projectId}_${timestamp}.zip`;

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast(`✅ Project "${projectTitle || "Untitled"}" exported successfully`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setToast(`❌ Export failed: ${message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SudoButton
      onClick={handleExport}
      disabled={isExporting}
      variant="secondary"
      size="small"
      className="text-xs px-3 py-1 hover:bg-blue-50 transition-colors"
      title={`Export complete project: ${projectTitle || "Untitled"}`}
    >
      {isExporting ? "⏳ Exporting..." : "📦 Export"}
    </SudoButton>
  );
};

interface FolderResult {
  id: string;
  title: string;
  isSeries: boolean;
  deleted: boolean;
  archived: boolean;
  projectIds: string[];
}

function Projects({ showUserColumn = true, userCache, user }: ProjectsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [results, setResults] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [projects, setProjects] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [queries, setQueries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [_allProjects, setAllProjects] = useState<any[]>([]);
  const [userFolders, setUserFolders] = useState<FolderResult[]>([]);
  const [matchedFolders, setMatchedFolders] = useState<FolderResult[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [titleMatchProjects, setTitleMatchProjects] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [folderGroups, setFolderGroups] = useState<{ folder: FolderResult; projects: any[] }[]>([]);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const { setToast } = useToast();

  const query = useCallback(
    async (page: number) => {
      const ref = adminRecords.collection("projects");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any;

      if (queries[page]) {
        q = queries[page];
      } else {
        q = ref.limit(25).orderBy("createdAt", "desc");

        if (page !== 1) {
          // look at previous page to build next query
          let result = results[page - 1];

          if (result) {
            q = q.startAfter(result[result.length - 1]);
          } else {
            setToast("Cannot construct a query for a page that is not adjacent to one already fetched");
          }
        }

        setQueries(update(queries, { [page]: { $set: q } }));

        //this.queries[page] = query;
      }

      if (user) {
        q = q.where("userId", "==", user.uid);
      }

      let querySnapshot = await q.get();

      if (showUserColumn && userCache) {
        await userCache.fetchIds(querySnapshot.docs.map((s: any) => s.get("userId")));
      }

      setProjects(querySnapshot.docs);

      setResults(update(results, { [page]: { $set: querySnapshot.docs } }));
      setLoading(false);
    },
    [queries, results, user, userCache, showUserColumn],
  );

  // Fetch folders for the user
  useEffect(() => {
    if (!user) {
      setUserFolders([]);
      return;
    }
    const fetchFolders = async () => {
      try {
        const response: { folders: FolderResult[] } =
          await Auth.fetchWithIdToken(
            `/api/admin/users/${user.uid}/folders`,
            {},
          );
        setUserFolders(response.folders || []);
      } catch {
        setUserFolders([]);
      }
    };
    fetchFolders();
  }, [user]);

  const searchAllProjects = useCallback(async (searchFor: string) => {
    if (!searchFor.trim()) {
      return;
    }

    setLoading(true);
    setMatchedFolders([]);
    setTitleMatchProjects([]);
    setFolderGroups([]);
    const term = searchFor.trim();
    const termLower = term.toLowerCase();

    // Collect all docs we'll need, keyed by ID
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const docsById = new Map<string, any>();

    // Try direct project document lookup by ID
    const docRef = adminRecords.collection("projects").doc(term);
    const docSnap = await docRef.get();
    if (docSnap.exists) {
      docsById.set(docSnap.id, docSnap);
    }

    // Fetch all user projects for title search
    if (user) {
      const q = adminRecords.collection("projects").where("userId", "==", user.uid).orderBy("createdAt", "desc");
      const querySnapshot = await q.get();
      setAllProjects(querySnapshot.docs);
      for (const doc of querySnapshot.docs) {
        docsById.set(doc.id, doc);
      }
    }

    // Find matching folders/series by name or ID
    const folders = userFolders.filter(
      (f) => f.id === term || f.title.toLowerCase().includes(termLower),
    );
    setMatchedFolders(folders);

    // Fetch any folder project docs we don't already have
    const missingFolderProjectIds = folders
      .flatMap((f) => f.projectIds)
      .filter((id) => !docsById.has(id));
    if (missingFolderProjectIds.length > 0) {
      const extraDocs = await Promise.all(
        missingFolderProjectIds.map((id) => adminRecords.collection("projects").doc(id).get()),
      );
      for (const doc of extraDocs) {
        if (doc.exists) {
          docsById.set(doc.id, doc);
        }
      }
    }

    // Build folder groups: each matched folder with its project docs
    const folderClaimedIds = new Set<string>();
    const groups = folders
      .map((folder) => {
        const folderDocs = folder.projectIds
          .map((id) => docsById.get(id))
          .filter(Boolean);
        for (const doc of folderDocs) {
          folderClaimedIds.add(doc.id);
        }
        return { folder, projects: folderDocs };
      })
      .filter((g) => g.projects.length > 0);

    // Title/ID matched projects that aren't already in a folder group
    const titleMatches = [...docsById.values()].filter((doc) => {
      if (folderClaimedIds.has(doc.id)) return false;
      const title = doc.get("title") || "";
      return doc.id === term || title.toLowerCase().includes(termLower);
    });

    if (showUserColumn && userCache) {
      const allDocs = [...titleMatches, ...groups.flatMap((g) => g.projects)];
      await userCache.fetchIds(allDocs.map((s) => s.get("userId")));
    }

    setTitleMatchProjects(titleMatches);
    setFolderGroups(groups);
    setProjects([...titleMatches, ...groups.flatMap((g) => g.projects)]);
    setLoading(false);
  }, [user, userCache, showUserColumn, userFolders]);

  // Trigger search when the debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      searchAllProjects(debouncedSearchTerm);
    } else {
      setMatchedFolders([]);
      setTitleMatchProjects([]);
      setFolderGroups([]);
      setProjects([]);
    }
  }, [debouncedSearchTerm, searchAllProjects]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      query(currentPage);
    }
  }, [currentPage, searchTerm, query]);

  const handleCopyId = useCallback(
    (id: string) => {
      setToast(`Copied ID to clipboard: ${id}`);
    },
    [setToast],
  );

  const columns = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseColumns: any[] = [
      {
        name: "ID",
        width: "220px",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selector: (row: any) => row.id,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => (
          <CopyToClipboard
            text={row.id}
            onCopy={() => handleCopyId(row.id)}
          >
            <div className="font-mono text-sm text-app-primary cursor-pointer hover:bg-app-primary-cta-transparent-15 px-2 py-1 rounded transition-colors">{row.id}</div>
          </CopyToClipboard>
        ),
        sortable: false,
      },
      {
        name: "Name",
        grow: 1,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selector: (row: any) => row.get("title"),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => (
          <div
            className="text-sm text-gray-900 font-medium truncate"
            title={row.get("title")}
          >
            {row.get("title") || "Untitled"}
          </div>
        ),
        sortable: false,
      },
      {
        name: "Created At",
        width: "220px",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selector: (row: any) => row.get("createdAt").toDate(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => (
          <div className="text-sm text-gray-600">
            {row.get("createdAt").toDate().toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        ),
        sortable: false,
      },
    ];

    if (showUserColumn) {
      baseColumns.push({
        name: "User",
        width: "250px",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selector: (row: any) => {
          let user = userCache?.getUser(row.get("userId"));
          return user ? user.displayName || user.email : "User Deleted";
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => {
          let user = userCache?.getUser(row.get("userId"));
          if (user) {
            return (
              <Link
                to={"/admin/user/" + user.email}
                className="text-app-primary hover:underline text-sm"
              >
                {user.displayName || user.email}
              </Link>
            );
          }
          return <span className="text-gray-500 text-sm">User Deleted</span>;
        },
        sortable: false,
      });
    }

    baseColumns.push(
      {
        name: "Status",
        width: "100px",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selector: (row: any) => (row.get("archiveDeleted") ? "Deleted" : row.get("deleted") ? "Trash" : "Active"),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => {
          const isArchiveDeleted = row.get("archiveDeleted");
          const isDeleted = row.get("deleted");
          const label = isArchiveDeleted ? "Deleted" : isDeleted ? "Trash" : "Active";
          const colorClass = isArchiveDeleted ? "bg-gray-100 text-gray-700" : isDeleted ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700";
          return <span className={`text-xs font-medium px-2 py-1 rounded ${colorClass}`}>{label}</span>;
        },
        sortable: false,
      },
      {
        name: "# Docs",
        width: "100px",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selector: (row: any) => row.get("contents").contents.length,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => <div className="text-sm text-gray-900 font-medium">{row.get("contents").contents.length}</div>,
        sortable: false,
      },
      {
        name: "Actions",
        width: "130px",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cell: (row: any) => (
          <ProjectExportPermission>
          <ExportButton
            projectId={row.id}
            projectTitle={row.get("title")}
          />
          </ProjectExportPermission>
        ),
        sortable: false,
        ignoreRowClick: true,
      },
    );

    return baseColumns;
  }, [handleCopyId, userCache, showUserColumn]);

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <input
          type="text"
          placeholder="Search by title, project ID, or folder/series name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-80 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-app-primary focus:border-transparent"
        />
        {debouncedSearchTerm.trim() && !loading && (
          <span className="text-sm text-gray-600">
            {titleMatchProjects.length + folderGroups.reduce((sum, g) => sum + g.projects.length, 0)} project{titleMatchProjects.length + folderGroups.reduce((sum, g) => sum + g.projects.length, 0) !== 1 ? "s" : ""} found
          </span>
        )}
        <ImportProjectSection user={user} />
      </div>
      {debouncedSearchTerm.trim() && !loading ? (
        <div className="flex flex-col gap-6">
          {titleMatchProjects.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Projects</span>
                <span className="text-xs text-gray-400">({titleMatchProjects.length})</span>
              </div>
              <DataTable
                columns={columns}
                responsive={true}
                noHeader
                noTableHead
                expandableRows
                expandableRowsComponent={<ExpandableComponent onSearchFolder={(title: string) => setSearchTerm(title)} />}
                pagination={false}
                data={titleMatchProjects}
              />
            </div>
          )}
          {folderGroups.map((group) => (
            <div key={group.folder.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${group.folder.isSeries ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                  {group.folder.isSeries ? "Series" : "Folder"}
                </span>
                <span className="text-sm font-medium text-gray-700">{group.folder.title}</span>
                <span className="text-xs text-gray-400">({group.projects.length} project{group.projects.length !== 1 ? "s" : ""})</span>
              </div>
              <DataTable
                columns={columns}
                responsive={true}
                noHeader
                noTableHead
                expandableRows
                expandableRowsComponent={<ExpandableComponent onSearchFolder={(title: string) => setSearchTerm(title)} />}
                pagination={false}
                data={group.projects}
              />
            </div>
          ))}
          {titleMatchProjects.length === 0 && folderGroups.length === 0 && (
            <div className="text-sm text-gray-500 py-8 text-center">No results found.</div>
          )}
        </div>
      ) : (
        <DataTable
          columns={columns}
          responsive={true}
          noHeader
          progressPending={loading}
          expandableRows
          expandableRowsComponent={<ExpandableComponent onSearchFolder={(title: string) => setSearchTerm(title)} />}
          pagination
          paginationServer
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25]}
          paginationTotalRows={99999999}
          onChangePage={(page: number) => {
            setPage(page);
          }}
          data={projects}
        />
      )}
      <div
        style={{ display: "none" }}
        id="editor"
      ></div>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DiagnoseDocRow({ doc, repairing, onRepair }: { doc: any; repairing: string | null; onRepair: (id: string, title: string) => void }) {
  const canRepair = doc.status === "CORRUPTED" || (doc.status === "error" && doc.snapshot?.available);

  return (
    <div className="px-4 py-3 border-t border-red-200 bg-red-50">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <span className="text-sm font-medium text-gray-900">{doc.title}</span>
          <span className="text-xs text-gray-400 font-mono ml-2">{doc.documentId}</span>
        </div>
        <SudoButton
          onClick={() => onRepair(doc.documentId, doc.title)}
          variant="primary"
          size="small"
          disabled={repairing === doc.documentId || !canRepair}
        >
          {repairing === doc.documentId ? "Repairing..." : "Repair"}
        </SudoButton>
      </div>

      {doc.status === "CORRUPTED" && (
        <div className="text-xs text-red-700 space-y-1">
          {doc.invalidNodes.length > 0 && (
            <p>Invalid node types found — {doc.invalidNodes.length} node{doc.invalidNodes.length === 1 ? "" : "s"} will be sanitized</p>
          )}
          {doc.schemaErrors?.length > 0 && (
            <p>Structural schema violations found — {doc.schemaErrors.length} error{doc.schemaErrors.length === 1 ? "" : "s"}</p>
          )}
          <details>
            <summary className="cursor-pointer text-red-500">Details</summary>
            <div className="mt-1 space-y-1">
              <p className="text-red-500">
                Node types: {Object.entries(doc.nodeTypeCounts).map(function([type, count]) { return type + ": " + count; }).join(", ")}
              </p>
              {doc.invalidNodes.map(function(node: any, idx: number) {
                return <pre key={idx} className="text-red-500 bg-red-100 p-1.5 rounded overflow-x-auto">{node.path}: {node.nodeType}</pre>;
              })}
              {doc.schemaErrors?.map(function(err: any, idx: number) {
                return <pre key={`schema-${idx}`} className="text-red-500 bg-red-100 p-1.5 rounded overflow-x-auto">{err.path} ({err.nodeType}): {err.error}</pre>;
              })}
            </div>
          </details>
        </div>
      )}

      {doc.status === "error" && (
        <div className="text-xs space-y-1.5">
          <p className="text-red-700">Y.js binary corrupted — <span className="font-mono">{doc.error}</span></p>
          {doc.dataSize != null && <p className="text-red-600">Size: {(doc.dataSize / 1024).toFixed(1)} KB</p>}
          {doc.snapshot && doc.snapshot.available && (
            <p className="text-green-700">
              Recovery: snapshot from {new Date(doc.snapshot.createdAt).toLocaleDateString()}
              {doc.snapshot.hasContent ? ` (~${doc.snapshot.wordCount} text nodes)` : " (empty)"}
            </p>
          )}
          {doc.snapshot && !doc.snapshot.available && (
            <p className="text-red-600 font-medium">No snapshot available — cannot auto-repair</p>
          )}
        </div>
      )}
    </div>
  );
}

function DiagnoseResultPanel({ result, repairing, onRepair }: { result: any; repairing: string | null; onRepair: (id: string, title: string) => void }) {
  const problemDocs = result.documents.filter(function(doc: any) { return doc.status === "CORRUPTED" || doc.status === "error"; });
  const allDocsWithSize = result.documents.filter(function(doc: any) { return doc.dataSize != null; });

  const headerClass = result.problemCount > 0 ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800";

  let headerText;
  if (result.problemCount > 0) {
    headerText = result.problemCount + " of " + result.totalDocuments + " document" + (result.problemCount === 1 ? "" : "s") + " need" + (result.problemCount === 1 ? "s" : "") + " repair";
  } else {
    headerText = "All " + result.totalDocuments + " documents are OK";
  }

  return (
    <div className="mt-4 border rounded-lg overflow-hidden">
      <div className={"px-4 py-2 text-sm font-medium " + headerClass}>
        {headerText}
      </div>

      {problemDocs.map(function(doc: any) {
        return <DiagnoseDocRow key={doc.documentId} doc={doc} repairing={repairing} onRepair={onRepair} />;
      })}

      <details className="border-t border-gray-200">
        <summary className="px-4 py-2 text-xs text-gray-500 cursor-pointer hover:bg-gray-50">
          All documents ({result.totalDocuments})
        </summary>
        <div className="px-4 pb-3">
          <table className="w-full text-xs">
            <tbody>
              {allDocsWithSize.map(function(doc: any) {
                const rowClass = doc.status !== "ok" ? "text-red-600 font-medium" : "text-gray-600";
                return (
                  <tr key={doc.documentId} className={rowClass}>
                    <td className="py-0.5 pr-3">{doc.title}</td>
                    <td className="py-0.5 text-right">{(doc.dataSize / 1024).toFixed(1)} KB</td>
                    <td className="py-0.5 pl-2">{doc.status !== "ok" ? doc.status : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

type OutlineStats = {
  itemCount: number;
  actCount: number;
  chapterCount: number;
  namedItemCount: number;
  chapterSummaryCount: number;
};

type OutlineSnapshot = {
  id: string;
  createdAt: string;
  itemPreview?: string;
  itemCount: number;
  stats?: OutlineStats;
  likelyCorrupt?: boolean;
};

const formatOutlineStats = (stats?: Partial<OutlineStats>) => {
  if (!stats) return "No stats";
  return [
    `${stats.itemCount || 0} items`,
    `${stats.actCount || 0} acts`,
    `${stats.chapterCount || 0} chapters`,
    `${stats.namedItemCount || 0} named`,
    `${stats.chapterSummaryCount || 0} summaries`,
  ].join(", ");
};

function OutlineRecoveryPanel({ projectId }: { projectId: string }) {
  const confirmAction = useConfirmation(projectId);
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [snapshotData, setSnapshotData] = useState<any>(null);
  const { setToast } = useToast();

  const loadSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Auth.fetchWithIdToken("/api/admin/outline/list-snapshots", { projectId });
      setSnapshotData(response);
      if (!response.success) {
        setToast(response.message || response.error || "Failed to load outline snapshots");
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to load outline snapshots";
      setToast(msg);
    } finally {
      setLoading(false);
    }
  }, [projectId, setToast]);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  const restoreSnapshot = async (snapshot: OutlineSnapshot) => {
    await confirmAction({
      title: "Restore outline snapshot",
      message: ["Restore this outline snapshot?", "", `Snapshot: ${snapshot.id}`, `Created: ${new Date(snapshot.createdAt).toLocaleString()}`, `Stats: ${formatOutlineStats(snapshot.stats)}`, "", "This will replace the current outline. A snapshot of the current outline will be kept first."].join("\n"),
      actionLabel: "Restore",
      destructive: true
    }, async () => {
      setWorkingId(`restore:${snapshot.id}`);
      try {
        const response = await Auth.fetchWithIdToken("/api/admin/outline/restore-snapshot", {
          projectId,
          snapshotId: snapshot.id
        });
        if (response.success) {
          setToast("Outline restored");
          await loadSnapshots();
        } else {
          setToast(response.message || response.error || "Failed to restore outline");
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Failed to restore outline";
        setToast(msg);
      } finally {
        setWorkingId(null);
      }
    });
  };

  const hideSnapshot = async (snapshot: OutlineSnapshot) => {
    await confirmAction({
      title: "Hide outline snapshot",
      message: ["Hide this outline snapshot from recovery lists?", "", `Snapshot: ${snapshot.id}`, `Created: ${new Date(snapshot.createdAt).toLocaleString()}`, `Stats: ${formatOutlineStats(snapshot.stats)}`, "", "Use this for corrupt snapshots that should not be restored again."].join("\n"),
      actionLabel: "Hide",
      destructive: true
    }, async () => {
      setWorkingId(`hide:${snapshot.id}`);
      try {
        const response = await Auth.fetchWithIdToken("/api/admin/outline/delete-snapshot", {
          projectId,
          snapshotId: snapshot.id
        });
        if (response.success) {
          setToast("Outline snapshot hidden");
          await loadSnapshots();
        } else {
          setToast(response.message || response.error || "Failed to hide outline snapshot");
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Failed to hide outline snapshot";
        setToast(msg);
      } finally {
        setWorkingId(null);
      }
    });
  };

  const clearCurrentOutline = async () => {
    await confirmAction({
      title: "Clear current outline",
      message: ["Clear the current outline?", "", `Current stats: ${formatOutlineStats(snapshotData?.outline?.currentStats)}`, "", "Only use this when no good snapshot is available. The current outline will be snapshotted first."].join("\n"),
      actionLabel: "Clear outline",
      destructive: true
    }, async () => {
      setWorkingId("clear-current");
      try {
        const response = await Auth.fetchWithIdToken("/api/admin/outline/clear-current", {
          projectId
        });
        if (response.success) {
          setToast("Outline cleared");
          await loadSnapshots();
        } else {
          setToast(response.message || response.error || "Failed to clear outline");
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Failed to clear outline";
        setToast(msg);
      } finally {
        setWorkingId(null);
      }
    });
  };

  const snapshots: OutlineSnapshot[] = snapshotData?.snapshots || [];
  const currentLikelyCorrupt = !!snapshotData?.outline?.currentLikelyCorrupt;

  return (
    <div className="mt-4 border rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 bg-gray-100 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Outline Recovery</h4>
          <p className="text-xs text-gray-600">
            Current outline: {formatOutlineStats(snapshotData?.outline?.currentStats)}
            {currentLikelyCorrupt ? <span className="ml-2 text-red-700 font-medium">Likely corrupt</span> : null}
          </p>
        </div>
        <div className="flex gap-2">
          <SudoButton
            onClick={loadSnapshots}
            variant="tertiary"
            size="small"
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </SudoButton>
          <SudoButton
            onClick={clearCurrentOutline}
            variant="danger"
            size="small"
            disabled={workingId === "clear-current" || !snapshotData?.success}
          >
            {workingId === "clear-current" ? "Clearing..." : "Clear Current Outline"}
          </SudoButton>
        </div>
      </div>

      {!snapshotData?.success && !loading && (
        <div className="px-4 py-3 text-sm text-red-700">
          {snapshotData?.message || snapshotData?.error || "Unable to load outline snapshots"}
        </div>
      )}

      {snapshotData?.success && snapshots.length === 0 && (
        <div className="px-4 py-3 text-sm text-gray-600">No outline snapshots found for this project.</div>
      )}

      {snapshotData?.success && snapshots.map((snapshot) => {
        const restoreWorking = workingId === `restore:${snapshot.id}`;
        const hideWorking = workingId === `hide:${snapshot.id}`;
        return (
          <div
            key={snapshot.id}
            className="px-4 py-3 border-t border-gray-200 flex flex-wrap items-start justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-gray-500">{snapshot.id}</span>
                <span className="text-xs text-gray-600">{new Date(snapshot.createdAt).toLocaleString()}</span>
                {snapshot.likelyCorrupt && <span className="text-xs font-medium text-red-700">Likely corrupt</span>}
              </div>
              <p className="text-xs text-gray-700 mt-1">{formatOutlineStats(snapshot.stats)}</p>
              {snapshot.itemPreview && <p className="text-xs text-gray-500 mt-1 truncate max-w-[640px]">{snapshot.itemPreview}</p>}
            </div>
            <div className="flex gap-2">
              <SudoButton
                onClick={() => restoreSnapshot(snapshot)}
                variant="primary"
                size="small"
                disabled={!!workingId}
              >
                {restoreWorking ? "Restoring..." : "Restore"}
              </SudoButton>
              <SudoButton
                onClick={() => hideSnapshot(snapshot)}
                variant="tertiary"
                size="small"
                disabled={!!workingId}
              >
                {hideWorking ? "Hiding..." : "Hide Snapshot"}
              </SudoButton>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const ExpandableComponent = ({ data, onSearchFolder }: { data?: any; onSearchFolder?: (title: string) => void }) => {
  const confirmAction = useConfirmation(data?.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [project, setProject] = useState<any>(null);
  const [folderInfo, setFolderInfo] = useState<{ id: string; title: string; isSeries: boolean; isRoot: boolean } | null>(null);
  const [repairing, setRepairing] = useState(false);
  const [legacyBeatsAction, setLegacyBeatsAction] = useState<"preview" | "apply" | null>(null);
  const [recalculatingWordCount, setRecalculatingWordCount] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState<any>(null);
  const [repairingDocId, setRepairingDocId] = useState<string | null>(null);

  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [outlineRecoveryOpen, setOutlineRecoveryOpen] = useState(false);
  const { setToast } = useToast();

  const refreshProject = useCallback(async () => {
    const record = await readAdminProject(data.id);
    const project = record ? new Project(record) : null;
    setProject(project);
  }, [data.id]);

  const fetchFolderInfo = useCallback(async () => {
    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/projects/${data.id}/folder-info`, {});
      if (response?.folder) {
        setFolderInfo(response.folder);
      }
    } catch {
      // Folder info is non-critical, ignore errors
    }
  }, [data.id]);

  useEffect(() => {
    refreshProject();
    fetchFolderInfo();
  }, [refreshProject, fetchFolderInfo]);

  const unlockProject = async (e: React.MouseEvent) => {
    e.preventDefault();
    const result = await Project.cancelSceneConversion(data.id);
    await result.update({ locked: false, adminUpdate: true });
    setToast("Project unlocked");
  };

  const restoreArchivedProject = async (e: React.MouseEvent) => {
    e.preventDefault();
    await confirmAction({
      title: "Restore project",
      message: `Are you sure you want to restore "${data.get("title")}"?\n\nThis project was permanently deleted by the user. Restoring it will make it visible on their dashboard again. This should only be done in rare circumstances at the user's request.`,
      actionLabel: "Restore",
      destructive: false
    }, async () => {
      await Project.restoreArchiveDeleted(data.id);
      setToast("Project restored");
    });
  };

  const diagnoseDocuments = async (e: React.MouseEvent) => {
    e.preventDefault();
    setDiagnosing(true);
    setDiagnoseResult(null);
    try {
      const response = await Auth.fetchWithIdToken("/api/admin/diagnose-project", {
        projectId: data.id,
      });
      setDiagnoseResult(response);
      if (response.problemCount > 0) {
        setToast(`Found ${response.problemCount} problem document(s)`);
      } else {
        setToast(`All ${response.totalDocuments} documents are OK`);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to diagnose documents";
      setToast(msg);
    } finally {
      setDiagnosing(false);
    }
  };

  const repairDocument = async (documentId: string, title: string) => {
    setRepairingDocId(documentId);
    try {
      // Step 1: Dry run — full pipeline without writing, includes round-trip verification
      const preview = await Auth.fetchWithIdToken("/api/admin/repair-document", {
        documentId,
        projectId: data.id,
        dryRun: true
      });
      if (!preview.success) {
        setToast("Repair not possible: " + (preview.error || "unknown error"));
        return;
      }
      const v = preview.verification;
      if (!v || !v.valid) {
        setToast("Repair dry run failed verification: " + (v?.error || "unknown"));
        return;
      }

      // Step 2: Show verified preview and confirm
      const lines = ["Strategy: " + preview.strategy, preview.snapshotDate ? "Snapshot: " + new Date(preview.snapshotDate).toLocaleDateString() : null, "New size: " + (preview.newSize / 1024).toFixed(1) + " KB (" + v.nodeCount + " nodes, " + v.textLength + " chars)", "", "Verified round-trip: encode -> decode -> OK", "", "Content preview:", v.preview].filter(Boolean).join("\n");
      await confirmAction({
        title: "Repair document",
        message: "Repair \"" + title + "\"?\n\n" + lines + "\n\nProceed with repair?",
        actionLabel: "Repair",
        destructive: false
      }, async () => {
        try {
          // Step 3: Execute for real
          const response = await Auth.fetchWithIdToken("/api/admin/repair-document", {
            documentId,
            projectId: data.id
          });
          if (response.success) {
            setToast("Repaired \"" + title + "\" — " + response.message);
            setDiagnoseResult(null);
            const updated = await Auth.fetchWithIdToken("/api/admin/diagnose-project", {
              projectId: data.id
            });
            setDiagnoseResult(updated);
          } else {
            setToast("Repair failed: " + (response.error || "unknown error"));
          }
        } catch (error) {
          const msg = error instanceof Error ? error.message : "Failed to repair document";
          setToast(msg);
        }
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to repair document";
      setToast(msg);
    } finally {
      setRepairingDocId(null);
    }
  };

  const projectLink = `${window.location.origin}/app/project/${data.id}`;
  const docNodes = project?.contents?.contents || data.get("contents").contents;

  const repairDeletedDocuments = async (e: React.MouseEvent) => {
    e.preventDefault();
    setRepairing(true);
    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/projects/${data.id}/repair-deleted-documents`, {});
      const restored = response?.restoredDocuments || 0;
      const reparented = response?.reparentedDocuments || 0;

      let message = "No document issues found to repair";
      if (restored > 0 || reparented > 0) {
        const parts: string[] = [];
        if (restored > 0) {
          parts.push(`restored ${restored} document${restored === 1 ? "" : "s"} from trash`);
        }
        if (reparented > 0) {
          parts.push(`moved ${reparented} misplaced document${reparented === 1 ? "" : "s"} back to the list`);
        }
        message = `${parts.join(" and ")}.`;
        message = message.charAt(0).toUpperCase() + message.slice(1);
      }

      setToast(message);
      await refreshProject();
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to repair project";
      setToast(msg);
    } finally {
      setRepairing(false);
    }
  };

  const clearLegacyBeats = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLegacyBeatsAction("preview");
    try {
      const endpoint = `/api/admin/projects/${data.id}/legacy-beats-cleanup`;
      const preview = (await Auth.fetchWithIdToken(endpoint, {
        dryRun: true
      })) as LegacyBeatsCleanupResponse;
      if (!preview.canApply || preview.scenesWithCommands === 0) {
        setToast("No legacy Beats data found; this project is already clean.");
        return;
      }
      const formatKb = (bytes: number) => `${(bytes / 1024).toFixed(1)} KB`;
      await confirmAction({
        title: "Clear legacy Beats",
        message: [`Clear legacy Beats from "${data.get("title")}"?`, "", `Story Engine: ${preview.storyEngineId}`, `Current size: ${formatKb(preview.currentBytes)}`, `Estimated size after cleanup: ${formatKb(preview.afterBytes)}`, `Scene command strings to archive and clear: ${preview.scenesWithCommands}`, `Legacy Beats text: ${formatKb(preview.commandsBytes)}`, "", "Every command string will be archived before it is cleared. Scene links are preserved.", "The user must reload the project after cleanup so their open session does not resend stale data."].join("\n"),
        actionLabel: "Clear legacy Beats",
        destructive: true
      }, async () => {
        try {
          setLegacyBeatsAction("apply");
          const result = (await Auth.fetchWithIdToken(endpoint, {
            dryRun: false,
            confirmation: `CLEAR LEGACY BEATS ${data.id}`
          })) as LegacyBeatsCleanupResponse;
          if (result.applied) {
            setToast(`Archived and cleared ${result.strippedCount || 0} legacy Beat${result.strippedCount === 1 ? "" : "s"}. Ask the user to reload the project.`);
          } else {
            setToast("No legacy Beats data found; this project is already clean.");
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : "Failed to clear legacy Beats";
          setToast(message);
        }
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to clear legacy Beats";
      setToast(message);
    } finally {
      setLegacyBeatsAction(null);
    }
  };

  const recalculateWordCount = async (e: React.MouseEvent) => {
    e.preventDefault();
    setRecalculatingWordCount(true);
    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/projects/${data.id}/recalculate-word-count`, {});
      const { oldWordCount, newWordCount, difference } = response;

      let message;
      if (difference === 0) {
        message = `Word count is correct: ${newWordCount.toLocaleString()} words`;
      } else {
        const sign = difference > 0 ? "+" : "";
        message = `Word count updated: ${oldWordCount.toLocaleString()} → ${newWordCount.toLocaleString()} (${sign}${difference.toLocaleString()})`;
      }

      setToast(message);
      await refreshProject();
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to recalculate word count";
      setToast(msg);
    } finally {
      setRecalculatingWordCount(false);
    }
  };

  return (
    <div className="bg-gray-50 p-6 border border-b-2 border-app-divider mb-1 rounded-b-lg">
      {/* Project Info */}
      <div className="mb-5 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <a
            href={projectLink}
            className="text-app-primary hover:underline font-medium transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.id}
          </a>
          <CopyToClipboardButton
            textToCopy={projectLink}
            successMessage={`Copied project link to clipboard`}
            useIcon
          />
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>
            <span className="font-medium text-gray-700">Words:</span> {(project?.words || data.get("words") || 0).toLocaleString()}
          </span>
          {folderInfo && !folderInfo.isRoot && (
            <button
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-solid cursor-pointer transition-all shadow-none ${
                folderInfo.isSeries
                  ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                  : "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
              }`}
              onClick={() => onSearchFolder?.(folderInfo.title)}
            >
              <SVG
                src={folderInfo.isSeries ? "/assets/series.svg" : "/assets/folder.svg"}
                width={12}
                height={12}
                className="opacity-60"
              />
              {folderInfo.title}
              <SVG
                src="/assets/search.svg"
                width={12}
                height={12}
                className="opacity-40"
              />
            </button>
          )}
        </div>
      </div>

      {/* Documents */}
      <div className="mb-5">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Documents ({docNodes.length})</h4>
        <div className="flex flex-col gap-1">
          {docNodes.map((doc: { id: string; title: string }) => (
            <div className="flex items-center gap-2 py-1" key={doc.id}>
              <CopyToClipboardButton
                key={`doc-${doc.id}`}
                textToCopy={doc.id}
                successMessage={`Copied ID to clipboard: ${doc.id}`}
                className="font-mono text-xs text-gray-500 bg-white border border-gray-200 rounded"
              />
              <span className="text-sm text-gray-700">{doc.title}</span>
            </div>
          ))}
        </div>
      </div>

      {agentSourceEntries(project?.data || data.data()).length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Agent Sources (hidden from users)</h4>
          <div className="flex flex-col gap-1">
            {agentSourceEntries(project?.data || data.data()).map((doc: { id: string; title: string }) => (
              <div className="flex items-center gap-2 py-1" key={doc.id}>
                <CopyToClipboardButton textToCopy={doc.id} successMessage={`Copied ID: ${doc.id}`} className="font-mono text-xs text-gray-500 bg-white border border-gray-200 rounded" />
                <Link
                  to={`/admin/document/${encodeURIComponent(doc.id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-app-primary hover:underline"
                >
                  {doc.title}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 flex-wrap items-center">
        {data.get("locked") && (
          <SudoTooltip text="This project is currently locked due to an in-progress scene conversion. Unlocking allows the user to edit again.">
            <SudoButton
              onClick={unlockProject}
              variant="primary"
              size="default"
            >
              Unlock Project
            </SudoButton>
          </SudoTooltip>
        )}
        <SudoTooltip text="Guided repair flow: diagnose, plan, and execute fixes in one place. Each action is dry-run before it writes.">
          <SudoButton
            onClick={() => setRepairModalOpen(true)}
            variant="primary"
            size="default"
          >
            Repair Project
          </SudoButton>
        </SudoTooltip>
        <SudoTooltip text="Restore a previous outline snapshot, hide corrupt outline snapshots, or clear the current outline when a project cannot open.">
          <SudoButton
            onClick={() => setOutlineRecoveryOpen((open) => !open)}
            variant="tertiary"
            size="default"
          >
            {outlineRecoveryOpen ? "Hide Outline Recovery" : "Outline Recovery"}
          </SudoButton>
        </SudoTooltip>
        <SudoTooltip text="Use when deleted docs remain or active docs go missing in trash. It syncs the current documents with Trash, restoring only documents still stuck there.">
          <SudoButton
            onClick={repairDeletedDocuments}
            variant="tertiary"
            size="default"
            disabled={repairing}
          >
            {repairing ? "Syncing…" : "Sync Documents"}
          </SudoButton>
        </SudoTooltip>
        {data.get("useScenes") === true && (
          <SudoTooltip text="For converted projects blocked by hidden legacy Beats data. Previews the size reduction, archives every command string, then clears only the obsolete commands while preserving scene links.">
            <SudoButton
              onClick={clearLegacyBeats}
              variant="tertiary"
              size="default"
              disabled={legacyBeatsAction !== null}
            >
              {legacyBeatsAction === "preview"
                ? "Checking Legacy Beats…"
                : legacyBeatsAction === "apply"
                  ? "Clearing Legacy Beats…"
                  : "Clear Legacy Beats"}
            </SudoButton>
          </SudoTooltip>
        )}
        <SudoTooltip text="Recalculates word count from all non-deleted documents and updates subscription total. Use when users report incorrect word counts.">
          <SudoButton
            onClick={recalculateWordCount}
            variant="tertiary"
            size="default"
            disabled={recalculatingWordCount}
          >
            {recalculatingWordCount ? "Recalculating…" : "Fix Word Count"}
          </SudoButton>
        </SudoTooltip>
        <SudoTooltip text="Reads all documents directly from the database and checks for corrupted Y.js binaries or invalid node types.">
          <SudoButton
            onClick={diagnoseDocuments}
            variant="tertiary"
            size="default"
            disabled={diagnosing}
          >
            {diagnosing ? "Scanning…" : "Scan Corrupt Docs"}
          </SudoButton>
        </SudoTooltip>
        {data.get("archiveDeleted") && (
          <>
            <div className="flex-1" />
            <SudoTooltip text="This project was permanently deleted by the user. Only restore at the user's explicit request and with clearance. This will make it visible on their dashboard again.">
              <SudoButton
                onClick={restoreArchivedProject}
                variant="danger"
                size="default"
              >
                Restore Deleted Project
              </SudoButton>
            </SudoTooltip>
          </>
        )}
      </div>
      {diagnoseResult && (
        <DiagnoseResultPanel
          result={diagnoseResult}
          repairing={repairingDocId}
          onRepair={repairDocument}
        />
      )}
      {outlineRecoveryOpen && <OutlineRecoveryPanel projectId={data.id} />}
      <RepairProjectModal
        projectId={data.id}
        projectTitle={data.get("title")}
        open={repairModalOpen}
        onOpenChange={setRepairModalOpen}
      />
    </div>
  );
};

export default Projects;
